/**
 * Business hours status based on contact/account location.
 * Uses city-timezones for worldwide city→timezone resolution,
 * with US state and country-level fallbacks.
 *
 * copied from mini-apps/action-view/business-hours.ts:1 (originally
 * byte-for-byte, including the GTMOPS-2165 disambiguation fix) — never
 * imported (mini-apps/CLAUDE.md); kept in lockstep by convention. See
 * `__tests__/business-hours.test.ts` (also copied) for the regression
 * coverage this carries.
 *
 * Deviation from the mini-apps copy: `getLocalTime`/`getLocalHour`/
 * `getBusinessHoursStatus` take an optional trailing `now: Date` (defaults
 * to `new Date()`, so every existing call site — including the mini-apps
 * copy's own tests — is unaffected). `SignalsFlowTable.tsx` passes a `now`
 * derived from `useMinuteToken()` so its `useMemo` dependency on that token
 * is real (the callback actually reads it) rather than an unread
 * lint-suppressed extra dep. If you re-sync this file from the mini-apps
 * original, re-apply this deviation.
 */
import cityTimezones from 'city-timezones';

export type BusinessHoursStatus = 'open' | 'closed' | 'unknown';

// US state → IANA timezone (most populous timezone per state)
const US_STATE_TZ: Record<string, string> = {
  AL: 'America/Chicago', AK: 'America/Anchorage', AZ: 'America/Phoenix',
  AR: 'America/Chicago', CA: 'America/Los_Angeles', CO: 'America/Denver',
  CT: 'America/New_York', DE: 'America/New_York', FL: 'America/New_York',
  GA: 'America/New_York', HI: 'Pacific/Honolulu', ID: 'America/Boise',
  IL: 'America/Chicago', IN: 'America/Indiana/Indianapolis', IA: 'America/Chicago',
  KS: 'America/Chicago', KY: 'America/New_York', LA: 'America/Chicago',
  ME: 'America/New_York', MD: 'America/New_York', MA: 'America/New_York',
  MI: 'America/Detroit', MN: 'America/Chicago', MS: 'America/Chicago',
  MO: 'America/Chicago', MT: 'America/Denver', NE: 'America/Chicago',
  NV: 'America/Los_Angeles', NH: 'America/New_York', NJ: 'America/New_York',
  NM: 'America/Denver', NY: 'America/New_York', NC: 'America/New_York',
  ND: 'America/Chicago', OH: 'America/New_York', OK: 'America/Chicago',
  OR: 'America/Los_Angeles', PA: 'America/New_York', RI: 'America/New_York',
  SC: 'America/New_York', SD: 'America/Chicago', TN: 'America/Chicago',
  TX: 'America/Chicago', UT: 'America/Denver', VT: 'America/New_York',
  VA: 'America/New_York', WA: 'America/Los_Angeles', WV: 'America/New_York',
  WI: 'America/Chicago', WY: 'America/Denver', DC: 'America/New_York',
};

// Country-level fallback for single/primary timezone countries
const COUNTRY_TZ: Record<string, string> = {
  'united kingdom': 'Europe/London', uk: 'Europe/London', gb: 'Europe/London',
  japan: 'Asia/Tokyo', jp: 'Asia/Tokyo',
  india: 'Asia/Kolkata', in: 'Asia/Kolkata',
  singapore: 'Asia/Singapore', sg: 'Asia/Singapore',
  germany: 'Europe/Berlin', de: 'Europe/Berlin',
  france: 'Europe/Paris', fr: 'Europe/Paris',
  israel: 'Asia/Jerusalem', il: 'Asia/Jerusalem',
  netherlands: 'Europe/Amsterdam', nl: 'Europe/Amsterdam',
  sweden: 'Europe/Stockholm', se: 'Europe/Stockholm',
  ireland: 'Europe/Dublin', ie: 'Europe/Dublin',
  'south korea': 'Asia/Seoul', kr: 'Asia/Seoul',
  'new zealand': 'Pacific/Auckland', nz: 'Pacific/Auckland',
  uae: 'Asia/Dubai', 'united arab emirates': 'Asia/Dubai', ae: 'Asia/Dubai',
  switzerland: 'Europe/Zurich', ch: 'Europe/Zurich',
  italy: 'Europe/Rome', it: 'Europe/Rome',
  spain: 'Europe/Madrid', es: 'Europe/Madrid',
  portugal: 'Europe/Lisbon', pt: 'Europe/Lisbon',
  poland: 'Europe/Warsaw', pl: 'Europe/Warsaw',
  norway: 'Europe/Oslo', no: 'Europe/Oslo',
  denmark: 'Europe/Copenhagen', dk: 'Europe/Copenhagen',
  finland: 'Europe/Helsinki', fi: 'Europe/Helsinki',
  colombia: 'America/Bogota', co: 'America/Bogota',
  chile: 'America/Santiago', cl: 'America/Santiago',
  argentina: 'America/Argentina/Buenos_Aires', ar: 'America/Argentina/Buenos_Aires',
  nigeria: 'Africa/Lagos', ng: 'Africa/Lagos',
  'south africa': 'Africa/Johannesburg', za: 'Africa/Johannesburg',
  egypt: 'Africa/Cairo', eg: 'Africa/Cairo',
  kenya: 'Africa/Nairobi', ke: 'Africa/Nairobi',
  philippines: 'Asia/Manila', ph: 'Asia/Manila',
  thailand: 'Asia/Bangkok', th: 'Asia/Bangkok',
  vietnam: 'Asia/Ho_Chi_Minh', vn: 'Asia/Ho_Chi_Minh',
  taiwan: 'Asia/Taipei', tw: 'Asia/Taipei',
};

/**
 * Country name → ISO2, for matching city-timezones' country/iso2/iso3 fields
 * against SFDC's free-text mailing country. The library's `country` field
 * uses long/canonical names (e.g. "United States of America") that never
 * match SFDC's shorter picklist values (e.g. "United States") — without this
 * alias, country-based disambiguation silently matches nothing and falls
 * through to whichever city the library happens to return first (GTMOPS-2165:
 * "San Jose, CA" resolving to San José, Costa Rica).
 */
const COUNTRY_NAME_TO_ISO2: Record<string, string> = {
  'united states': 'us', 'united states of america': 'us', usa: 'us', 'u.s.': 'us', 'u.s.a.': 'us',
  'united kingdom': 'gb', uk: 'gb', 'u.k.': 'gb', 'great britain': 'gb',
};

// Common SFDC city names that differ from the library's canonical names
const CITY_ALIASES: Record<string, string> = {
  bangalore: 'Bengaluru',
  'sao paulo': 'Sao Paulo',
  'são paulo': 'Sao Paulo',
  bombay: 'Mumbai',
  madras: 'Chennai',
  calcutta: 'Kolkata',
  peking: 'Beijing',
  'ho chi minh city': 'Ho Chi Minh City',
  saigon: 'Ho Chi Minh City',
};

function normalizeCity(city: string): string {
  const cleaned = city
    .replace(/metropolitan area/gi, '')
    .replace(/\bgreater\b/gi, '')
    .trim();
  return CITY_ALIASES[cleaned.toLowerCase()] ?? cleaned;
}

/**
 * Look up timezone via city-timezones library.
 * Searches both display name and ascii name, disambiguates by country/state.
 */
function lookupCityTimezone(
  city: string,
  state: string | null | undefined,
  country: string | null | undefined,
): string | null {
  const normalized = normalizeCity(city);

  // Try the library's built-in lookup first
  let results = cityTimezones.lookupViaCity(normalized);

  // Also try ascii search if no results (handles diacritics like Zürich vs Zurich,
  // and partial names like "Tel Aviv" matching "Tel Aviv-Yafo", "Bangalore" → "Bengaluru")
  if (results.length === 0) {
    const lower = normalized.toLowerCase();
    results = cityTimezones.cityMapping.filter(
      (c: { city_ascii?: string }) => c.city_ascii?.toLowerCase() === lower,
    );
    // Fuzzy: check if the query is a prefix or substring of the ascii name
    if (results.length === 0) {
      results = cityTimezones.cityMapping.filter(
        (c: { city_ascii?: string }) => c.city_ascii?.toLowerCase().startsWith(lower),
      );
    }
  }

  if (results.length === 0) return null;
  if (results.length === 1) return results[0].timezone;

  // Disambiguate CUMULATIVELY — narrow the candidate set at each step
  // instead of early-returning on the first non-empty filter. A country
  // match alone can still be ambiguous (multiple "San Jose"s inside the US),
  // so state narrows further within whatever country matched, rather than
  // either filter picking arbitrarily on its own.
  let candidates = results;

  if (country) {
    const countryLower = country.toLowerCase();
    const iso2 = COUNTRY_NAME_TO_ISO2[countryLower] ?? countryLower;
    const byCountry = candidates.filter(
      (r: { country?: string; iso2?: string; iso3?: string }) =>
        r.country?.toLowerCase() === countryLower ||
        r.iso2?.toLowerCase() === countryLower ||
        r.iso3?.toLowerCase() === countryLower ||
        r.iso2?.toLowerCase() === iso2,
    );
    if (byCountry.length > 0) candidates = byCountry;
  }

  // Disambiguate by state/province (for US cities with common names). Match
  // both the 2-letter ANSI code ("CA") and the full province name
  // ("California") — SFDC state values appear in both forms.
  if (state && candidates.length > 1) {
    const stateLower = state.toLowerCase();
    const byState = candidates.filter(
      (r: { province?: string; state_ansi?: string }) =>
        r.state_ansi?.toLowerCase() === stateLower || r.province?.toLowerCase() === stateLower,
    );
    if (byState.length > 0) candidates = byState;
  }

  if (candidates.length === 1) return candidates[0].timezone;

  // Still ambiguous after narrowing. For a recognized US state we already
  // have a reliable, DST-safe fallback (US_STATE_TZ) — defer to it (via
  // resolveTimezone's next step) rather than guessing among unresolved
  // international candidates by population.
  const stateCode = state && state.length === 2 ? state.toUpperCase() : null;
  if (stateCode && US_STATE_TZ[stateCode]) return null;

  // Otherwise, best remaining guess: highest population match.
  return [...candidates].sort((a, b) => b.pop - a.pop)[0]?.timezone ?? null;
}

function resolveTimezone(
  city: string | null | undefined,
  state: string | null | undefined,
  country: string | null | undefined,
): string | null {
  // Try city lookup via library
  if (city) {
    const tz = lookupCityTimezone(city, state, country);
    if (tz) return tz;
  }

  // Try US state fallback
  if (state) {
    const stateCode = state.length === 2 ? state.toUpperCase() : null;
    if (stateCode && US_STATE_TZ[stateCode]) return US_STATE_TZ[stateCode];
  }

  // Country-level fallback
  if (country) {
    const tz = COUNTRY_TZ[country.toLowerCase()];
    if (tz) return tz;
  }

  return null;
}

function getHourInTimezone(tz: string, now: Date): number | null {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour: 'numeric',
      hour12: false,
    });
    return Number(formatter.format(now));
  } catch {
    return null;
  }
}

/**
 * Get the contact's local time as a formatted string (e.g. "2:34 PM"), or null if unknown.
 * `now` defaults to the real wall clock; callers that re-derive on a timer (Terra's
 * `SignalsFlowTable`) pass it explicitly so the dependency driving that re-derivation is real.
 */
export function getLocalTime(
  city: string | null | undefined,
  state: string | null | undefined,
  country: string | null | undefined,
  now: Date = new Date(),
): string | null {
  const tz = resolveTimezone(city, state, country);
  if (!tz) return null;
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(now);
  } catch {
    return null;
  }
}

/** Get the contact's local hour as a number (0-23), or null if unknown. Used for sorting. */
export function getLocalHour(
  city: string | null | undefined,
  state: string | null | undefined,
  country: string | null | undefined,
  now: Date = new Date(),
): number | null {
  const tz = resolveTimezone(city, state, country);
  if (!tz) return null;
  return getHourInTimezone(tz, now);
}

export function getBusinessHoursStatus(
  city: string | null | undefined,
  state: string | null | undefined,
  country: string | null | undefined,
  now: Date = new Date(),
): BusinessHoursStatus {
  const tz = resolveTimezone(city, state, country);
  if (!tz) return 'unknown';

  const hour = getHourInTimezone(tz, now);
  if (hour == null) return 'unknown';

  // Business hours: 8 AM – 6 PM local
  return hour >= 8 && hour < 18 ? 'open' : 'closed';
}
